import { Espin_Role_Enum, AllProfileViewsCardFragment, Espin_Profile_View_Type_Enum } from 'generated/spin-graphql';
import Link from 'next/link';
import React, { useCallback, useMemo } from 'react';
import { useAppState } from 'client/providers/AppProvider';
import { SocialUserCard } from '../SocialUserCard';
import { SocialUserInfo } from './ProfileViews';
import { gql } from '@urql/core';

type Props = {
    profileViews?: AllProfileViewsCardFragment[];
    totalViews: number;
    childrenSubscribed?: boolean;
    listOfFollows: string[];
    handleFollowButtonClicked: (userId: string, following: boolean, viewType?: Espin_Profile_View_Type_Enum) => void;
};

gql`
    fragment AllProfileViewsCard on espin_profile_view {
        id
        college_id
        college_user_id
        college_user_full_name
        college {
            id
            slug
            name
            logo {
                ...SpinContentImageDisplay
            }
        }
        college_user {
            deleted_at
        }
        parent_user_id
        player_user {
            id
            deleted_at
            player_profile {
                id
                first_name
                last_name
                player_games {
                    primary
                    game {
                        name
                        game_definition
                    }
                }
                profile_image {
                    ...SpinContentImageDisplay
                }
                slug
                graduating_class
            }
        }
        high_school {
            id
            slug
            name
            contact_name
            profile_pic_file {
                ...SpinContentImageDisplay
            }
        }
        high_school_user {
            deleted_at
        }
    }
`;

export const AllProfileViewsCard: React.FC<Props> = ({
    profileViews,
    totalViews,
    childrenSubscribed,
    listOfFollows,
    handleFollowButtonClicked,
}) => {
    const { user, subscribed } = useAppState();

    const getFakeCards = useCallback(() => {
        const fakeData: SocialUserInfo = {
            slug: '',
            picture: null,
            label: 'A user viewed your profile',
            subLabel: 'Upgrade to Premium to see who viewed your profile!',
            following: false,
            fakeUser: true,
            messageSlug: '',
            deletedUser: false,
        };
        const fakeCards: JSX.Element[] = [];

        for (let i = 0; i < Math.min(20, totalViews); ++i) {
            const element = (
                <SocialUserCard
                    key={`fake-profile-view-card-${i}`}
                    socialUserInfo={fakeData}
                    onFollowClicked={() => {}}
                />
            );
            fakeCards.push(element);
        }
        return fakeCards;
    }, [totalViews]);

    const notSubbed = useMemo(
        () => (
            <div className={`content inactive`}>
                {getFakeCards()}
                <div className="unsubbed-container">
                    <div className="cta-container">
                        <h2 className="title">Unlock Profile Views</h2>
                        <p className="subtitle">Upgrade to Premium to see who viewed your profile!</p>
                        <Link href="/dashboard/subscriptions">
                            <a className="button medium-button blue-button">GET PREMIUM</a>
                        </Link>
                    </div>
                </div>
            </div>
        ),
        [getFakeCards],
    );

    const renderCardType = useCallback(
        (profileView: AllProfileViewsCardFragment, index: number) => {
            let socialUserInfo: SocialUserInfo;
            let hasGraduatingClass = true;
            let idToUnfollow: string;
            let following = false;
            let viewType = undefined;
            if (profileView?.college_user_id) {
                const profile = profileView.college;
                // college could be null if college was removed or non-verified
                if (!!!profile) {
                    socialUserInfo = {
                        following,
                        label: `Unverified College`,
                        picture: undefined,
                        subLabel: '',
                        slug: `/colleges/`,
                        fakeUser: false,
                        messageSlug: ``,
                    };
                    hasGraduatingClass = false;
                    idToUnfollow = profileView.college_id;
                } else {
                    following = listOfFollows.includes(profileView.college_id);
                    socialUserInfo = {
                        following,
                        label: `${profile.name}`,
                        picture: profile.logo,
                        subLabel: profileView.college_user_full_name ?? '',
                        slug: `/colleges/${profile.slug}`,
                        fakeUser: false,
                        messageSlug: `?college_slug=${profile.slug}`,
                        deletedUser: !!profileView.player_user?.deleted_at ? true : false,
                    };
                    hasGraduatingClass = false;
                    idToUnfollow = profileView.college_id;
                }
            } else if (profileView?.parent_user_id && user.role === Espin_Role_Enum.EspinCollegeUser) {
                const playerUser = profileView.player_user;
                const profile = playerUser?.player_profile;

                if (!playerUser || !profile) {
                    socialUserInfo = {
                        following: false,
                        label: 'Unknown Player',
                        picture: undefined,
                        subLabel: '',
                        slug: '/players',
                        fakeUser: false,
                        messageSlug: '',
                        deletedUser: true,
                    };
                    hasGraduatingClass = false;
                    idToUnfollow = playerUser?.id ?? '';
                } else {
                    const primaryGame = profile.player_games?.find((f) => f.primary);
                    following = listOfFollows.includes(playerUser.id);
                    socialUserInfo = {
                        following,
                        label: `${profile.first_name} ${profile.last_name}'s Parent`,
                        picture: profile.profile_image,
                        subLabel: primaryGame?.game.name ?? 'No Game',
                        game: primaryGame?.game,
                        graduating_class: profile.graduating_class,
                        slug: `/players/${profile.slug}`,
                        fakeUser: false,
                        messageSlug: `?player_slug=${profile.slug}`,
                        deletedUser: !!playerUser.deleted_at ? true : false,
                    };
                    idToUnfollow = playerUser.id;
                    viewType = Espin_Profile_View_Type_Enum.ParentViewsCollege;
                }
            } else if (profileView.high_school && user.role === Espin_Role_Enum.EspinCollegeUser) {
                const profile = profileView.high_school;
                following = listOfFollows.includes(profileView.high_school.id);
                socialUserInfo = {
                    following,
                    label: `${profile.name}`,
                    picture: profile.profile_pic_file,
                    subLabel: `${profile.contact_name}`,
                    slug: `/high-schools/${profile.slug}`,
                    fakeUser: false,
                    messageSlug: `?high_school_slug=${profile.slug}`,
                    deletedUser: !!profileView.high_school_user?.deleted_at ? true : false,
                };
                hasGraduatingClass = false;
                idToUnfollow = profileView.high_school.id;
                viewType = Espin_Profile_View_Type_Enum.HighSchoolViewsCollege;
            } else {
                const playerUser = profileView.player_user;
                const profile = playerUser?.player_profile;

                if (!playerUser || !profile) {
                    socialUserInfo = {
                        following: false,
                        label: 'Unknown Player',
                        picture: undefined,
                        subLabel: '',
                        slug: '/players',
                        fakeUser: false,
                        messageSlug: '',
                        deletedUser: true,
                    };
                    hasGraduatingClass = false;
                    idToUnfollow = playerUser?.id ?? '';
                } else {
                    const primaryGame = profile.player_games?.find((f) => f.primary);
                    following = listOfFollows.includes(playerUser.id);
                    socialUserInfo = {
                        following,
                        label: `${profile.first_name} ${profile.last_name}`,
                        picture: profile.profile_image,
                        subLabel: primaryGame?.game.name ?? 'No Game',
                        game: primaryGame?.game,
                        graduating_class: profile.graduating_class,
                        slug: `/players/${profile.slug}`,
                        fakeUser: false,
                        messageSlug: `?player_slug=${profile.slug}`,
                        deletedUser: !!playerUser.deleted_at ? true : false,
                    };
                    idToUnfollow = playerUser.id;
                    viewType = Espin_Profile_View_Type_Enum.PlayerViewsCollege;
                }
            }

            return (
                <SocialUserCard
                    key={`profile-view-card-${index}`}
                    socialUserInfo={socialUserInfo}
                    hasGraduatingClass={hasGraduatingClass}
                    onFollowClicked={() => handleFollowButtonClicked(idToUnfollow, following, viewType)}
                />
            );
        },
        [user.role, listOfFollows, handleFollowButtonClicked],
    );

    const profileViewList = useMemo(
        () => (
            <div className={`content${!profileViews ? ' inactive' : ''}`}>
                {profileViews &&
                    profileViews.length > 0 &&
                    (subscribed || childrenSubscribed || user?.role === Espin_Role_Enum.EspinHighschoolUser) &&
                    profileViews.map((profileView, index: number) => renderCardType(profileView, index))}
            </div>
        ),
        [profileViews, renderCardType, subscribed, childrenSubscribed, user?.role],
    );

    const conditionalRendering = useCallback(
        (role: Espin_Role_Enum) => {
            switch (role) {
                case Espin_Role_Enum.EspinCollegeUser:
                    if (!subscribed) return notSubbed;
                    return profileViewList;
                case Espin_Role_Enum.EspinPlayerUser:
                    if (!subscribed) return notSubbed;
                    return profileViewList;
                case Espin_Role_Enum.EspinParentUser:
                    if (!childrenSubscribed) return notSubbed;
                    return profileViewList;
                case Espin_Role_Enum.EspinHighschoolUser:
                    return profileViewList;
            }
        },
        [notSubbed, profileViewList, subscribed, childrenSubscribed],
    );

    return (
        <div className="all-profile-views-card">
            <div className="title-container">
                <h2 className="title">All Profile Views</h2>
            </div>
            <>{conditionalRendering(user?.role)}</>
        </div>
    );
};
